/**
 * BOM Lookup — filterable Buyer SKU table with Cost, Selling Price, Margin,
 * and an incomplete-BOM marker. Each row expands inline to reveal its full
 * BOM with per-RM line cost.
 *
 * Spec: see Jun 2026 redesign brief. The persistent table replaces the
 * older single-SKU drill-down view (which lives on as a fallback when
 * arriving via the deep-link `/bom-lookup?sku=<id>`).
 *
 * Pricing columns (Cost / Selling Price / Margin / Status) and the
 * unit-price column in the expand panel are RBAC-gated behind the same
 * Forecast/Reports access as `Reports → Buyer SKU BOM Cost`. Users
 * without that gate see qty + RM only.
 */
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import {
  Search, ChevronRight, ChevronDown, AlertTriangle, CheckCircle2, Circle,
  Loader2, Download, X, ExternalLink, Lock, FileText, RefreshCw,
} from "lucide-react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import useAuthStore from "@/store/authStore";
import BomRowExportPopover from "@/components/BomRowExportPopover";
import BomHistoryDialog from "@/components/BomHistoryDialog";
import { History } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_CHIPS = [
  { key: "ALL", label: "All", icon: null, cls: "" },
  { key: "complete", label: "Complete", icon: CheckCircle2, cls: "border-emerald-300 text-emerald-700 bg-emerald-50" },
  { key: "incomplete", label: "Incomplete BOM", icon: AlertTriangle, cls: "border-amber-300 text-amber-700 bg-amber-50" },
  { key: "no_bom", label: "No BOM", icon: Circle, cls: "border-zinc-300 text-zinc-700 bg-zinc-50" },
  { key: "no_price", label: "No price", icon: AlertTriangle, cls: "border-violet-300 text-violet-700 bg-violet-50" },
  // Jul 2026 — vendor-manufactured SKUs whose cost comes from vendor_fg_prices
  // (agreed) or realized fg_inventory rates rather than an RM BOM rollup.
  { key: "vendor_fg", label: "Vendor FG", icon: CheckCircle2, cls: "border-sky-300 text-sky-700 bg-sky-50" },
];

const fmtINR = (v) => {
  if (v == null) return "—";
  return `₹ ${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const marginColour = (pct) => {
  if (pct == null) return "text-zinc-400";
  if (pct >= 25) return "text-emerald-700 font-semibold";
  if (pct >= 10) return "text-amber-600 font-semibold";
  return "text-rose-700 font-semibold";
};

const statusBadge = (status, missingCount) => {
  if (status === "complete") {
    return <Badge variant="outline" className="border-emerald-300 text-emerald-700 bg-emerald-50 text-[10px]" data-testid="status-chip-complete">🟢 Complete</Badge>;
  }
  if (status === "incomplete") {
    return <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 text-[10px]" data-testid="status-chip-incomplete">🟠 Incomplete · {missingCount || 0}</Badge>;
  }
  if (status === "no_bom") {
    return <Badge variant="outline" className="border-zinc-300 text-zinc-600 bg-zinc-50 text-[10px]" data-testid="status-chip-no-bom">⚪ No BOM</Badge>;
  }
  if (status === "no_price") {
    return <Badge variant="outline" className="border-violet-300 text-violet-700 bg-violet-50 text-[10px]" data-testid="status-chip-no-price">🟣 No price</Badge>;
  }
  if (status === "vendor_fg") {
    // Jul 2026 — cost sourced from FG price master (vendor_fg_prices) or
    // realized fg_inventory rate. Rendered same as the other pricing
    // badges; RBAC-gated because it lives inside `showPricingCols`.
    return <Badge variant="outline" className="border-sky-300 text-sky-700 bg-sky-50 text-[10px]" data-testid="status-chip-vendor-fg">🔵 Vendor FG</Badge>;
  }
  return null;
};

const BomLookup = () => {
  const token = useAuthStore((s) => s.token);
  const hasRole = useAuthStore((s) => s.hasRole);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // RBAC: pricing columns gate on `Forecast:READ`, the master
  // planning permission (seed_rbac.py grants it to every role via
  // `"roles": ["*"]`, so real seeded roles like FINANCE_OPERATIONS
  // pick it up). Previously this check referenced two identifiers
  // that were never seeded, making both branches always-false and
  // effectively locking pricing to MASTER_ADMIN + TECH_OPS_ENGINEER
  // regardless of the RBAC grants applied to a user (Feb 2026 RCA).
  const canSeePricing = useMemo(
    () => hasPermission("Forecast", "READ"),
    [hasPermission],
  );

  // ---- Filters ----
  const [verticals, setVerticals] = useState([]);
  const [models, setModels] = useState([]);
  const [brands, setBrands] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [filterVertical, setFilterVertical] = useState("ALL");
  const [filterModel, setFilterModel] = useState("ALL");
  const [filterBrand, setFilterBrand] = useState("ALL");
  const [filterLicense, setFilterLicense] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // ---- Data ----
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  // Feb 2026 — additive Export-BOM modal. Single-SKU-scoped by
  // construction (path param on the server endpoint) so the "PDF dumps
  // every SKU" bug can't recur through this button.
  // Feb 2026 — single-SKU export moved to per-row popover
  // (BomRowExportPopover). The header modal has been removed; this
  // page no longer holds any single-SKU export state.
  // Jul 2026 — Bulk "Export All BOMs (entire master)". Backend endpoint
  // /api/bom/export-all decides the cost variant server-side (master
  // admin + include_cost=true → costed file; anyone else → cost-free
  // file). Frontend hides the "with costs" affordance for non-admins;
  // the server enforces the real gate.
  const [downloadingAll, setDownloadingAll] = useState(false);
  const [historySkuId, setHistorySkuId] = useState(null);
  // Feb 2026 — cache status for the "Export All BOMs" button.
  // { status: NEVER|BUILDING|READY|FAILED, generated_at, row_count, sku_count, error? }
  const [cacheMeta, setCacheMeta] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const isMasterAdmin = hasRole("MASTER_ADMIN");
  const [bomCache, setBomCache] = useState({});  // buyer_sku_id -> {items, total_cost}
  const [bomLoading, setBomLoading] = useState({});

  // ---- Deep-link support ----
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSkuParam = searchParams.get("sku");
  const initialApplied = useRef(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchText), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  // Cascade filters
  const fetchFilters = useCallback(async () => {
    try {
      const [v, l] = await Promise.all([
        // Backend canonical endpoint is /api/verticals (no /master-data
        // namespace was ever created — earlier code used a phantom path
        // that silently 404'd and left the dropdown blank).
        axios.get(`${API}/verticals`, { headers }).catch((e) => {
          console.error("Failed to load verticals", e);
          toast.error("Failed to load verticals");
          return { data: [] };
        }),
        axios.get(`${API}/bom-lookup/licenses`, { headers }).catch((e) => {
          console.error("Failed to load licenses", e);
          toast.error("Failed to load licenses");
          return { data: { licenses: [] } };
        }),
      ]);
      const vArr = Array.isArray(v.data) ? v.data : (v.data?.items || []);
      // Backend returns full vertical docs; normalise so the Select
      // renderer below still finds {id?, name}.
      setVerticals(vArr.filter(x => x && x.name));
      setLicenses(l.data?.licenses || []);
    } catch (e) {
      console.error("Failed to load filters", e);
      toast.error("Failed to load filters");
    }
  }, [headers]);

  const fetchModelsFor = useCallback(async (vertName) => {
    if (vertName === "ALL") { setModels([]); return; }
    try {
      // Canonical endpoint: GET /api/skus/models-by-vertical?vertical=...
      // Response shape is { models: [<string>, ...] } — NOT a bare array.
      const r = await axios.get(`${API}/skus/models-by-vertical`, {
        params: { vertical: vertName }, headers,
      });
      // Wrap into {name} objects so the renderer's `m.name` access still works.
      setModels((r.data?.models || []).map(name => ({ name })));
    } catch (e) {
      console.error("Failed to load models", e);
      toast.error("Failed to load models");
      setModels([]);
    }
  }, [headers]);

  const fetchBrandsFor = useCallback(async () => {
    // Brands cascade off Vertical (and optionally Model). With no
    // vertical chosen we leave the dropdown empty rather than hammer the
    // backend for the entire brand universe.
    if (filterVertical === "ALL") { setBrands([]); return; }
    try {
      const params = { vertical: filterVertical };
      if (filterModel !== "ALL") params.model = filterModel;
      const r = await axios.get(`${API}/skus/brands-by-vertical-model`, { params, headers });
      setBrands((r.data?.brands || []).map(name => ({ name })));
    } catch (e) {
      console.error("Failed to load brands", e);
      toast.error("Failed to load brands");
      setBrands([]);
    }
  }, [filterVertical, filterModel, headers]);

  useEffect(() => { fetchFilters(); }, [fetchFilters]);
  useEffect(() => {
    fetchModelsFor(filterVertical);
    // Reset dependent filters whenever Vertical changes so we never carry
    // a stale Model/Brand that doesn't belong to the new Vertical.
    setFilterModel("ALL");
    setFilterBrand("ALL");
  }, [filterVertical, fetchModelsFor]);
  // Re-fetch brands whenever Vertical or Model changes (cascading filter).
  useEffect(() => { fetchBrandsFor(); }, [fetchBrandsFor]);

  // Main fetch — search-with-cost
  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterVertical !== "ALL") params.vertical = filterVertical;
      if (filterModel !== "ALL") params.model = filterModel;
      if (filterBrand !== "ALL") params.brand = filterBrand;
      if (filterLicense !== "ALL") params.license = filterLicense;
      if (debouncedSearch) params.q = debouncedSearch;
      if (filterStatus !== "ALL") params.bom_status = filterStatus;
      params.limit = 2000;
      const r = await axios.get(`${API}/bom-lookup/search-with-cost`, { params, headers });
      setRows(r.data.results || []);
      setTotals(r.data.totals || null);
    } catch (e) {
      toast.error("Failed to fetch BOM lookup data");
      setRows([]);
      setTotals(null);
    }
    setLoading(false);
  }, [filterVertical, filterModel, filterBrand, filterLicense, filterStatus, debouncedSearch, headers]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  // Per-row BOM detail loader
  const loadBom = useCallback(async (buyerSkuId) => {
    if (bomCache[buyerSkuId]) return;
    setBomLoading((m) => ({ ...m, [buyerSkuId]: true }));
    try {
      const r = await axios.get(`${API}/rm-prices/buyer-sku-cost-detail/${buyerSkuId}`, { headers });
      setBomCache((m) => ({ ...m, [buyerSkuId]: r.data }));
    } catch (_e) {
      // Fallback to /bom (no pricing). Branch Ops users land here.
      try {
        const r2 = await axios.get(`${API}/bom-lookup/bom/${buyerSkuId}`, { headers });
        setBomCache((m) => ({ ...m, [buyerSkuId]: {
          items: (r2.data.bom || []).map((b) => ({
            rm_id: b.rm_id,
            rm_name: b.description,
            category: b.category,
            quantity: b.quantity_per_unit,
            unit: b.unit,
            bom_source: b.bom_source,
            avg_price: null,
            line_cost: null,
            price_source: null,
          })),
          total_cost: null,
        }}));
      } catch (_e2) { /* swallow */ }
    }
    setBomLoading((m) => ({ ...m, [buyerSkuId]: false }));
  }, [headers, bomCache]);

  // Deep-link: when the page loads with ?sku=X and the row arrives, expand
  // it AND kick off the BOM fetch. Placed after `loadBom` to dodge the
  // temporal-dead-zone issue.
  useEffect(() => {
    if (initialApplied.current || !initialSkuParam || !rows.length) return;
    if (rows.find((r) => r.buyer_sku_id === initialSkuParam)) {
      setExpanded(new Set([initialSkuParam]));
      loadBom(initialSkuParam);
      initialApplied.current = true;
    }
  }, [initialSkuParam, rows, loadBom]);

  const toggleExpand = (buyerSkuId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(buyerSkuId)) next.delete(buyerSkuId);
      else { next.add(buyerSkuId); loadBom(buyerSkuId); }
      return next;
    });
    // Keep deep-link in sync with the most recent open row.
    if (!expanded.has(buyerSkuId)) {
      setSearchParams({ sku: buyerSkuId }, { replace: true });
    }
  };

  const clearFilters = () => {
    setFilterVertical("ALL"); setFilterModel("ALL"); setFilterBrand("ALL");
    setFilterLicense("ALL"); setFilterStatus("ALL"); setSearchText("");
  };

  // ---- Excel exports ----
  const exportMain = () => {
    if (!rows.length) { toast.error("No rows to export"); return; }
    const data = rows.map((r) => ({
      "Buyer SKU ID": r.buyer_sku_id,
      "Name": r.name || "",
      "Vertical": r.vertical || "",
      "Model": r.model || "",
      "License": r.license_code || "",
      "Brand": r.brand || "",
      ...(canSeePricing ? {
        "Cost (₹)": r.total_cost ?? "",
        "Cost Source": r.cost_source || "",
        "RM Cost (₹)": r.rm_cost ?? "",
        "Vendor FG Cost (₹)": r.vendor_fg_cost ?? "",
        "Selling Price (₹)": r.selling_price ?? "",
        "Margin %": r.margin_pct ?? "",
        "Status": r.bom_status,
        "Missing Price Count": r.missing_price_count ?? 0,
        "Customer Count": r.selling_price_customer_count ?? 0,
      } : {}),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BOM Lookup");
    const buf = XLSX.write(wb, { type: "array", bookType: "xlsx" });
    saveAs(new Blob([buf]), `bom_lookup_${Date.now()}.xlsx`);
  };

  // Jul 2026 — Bulk master-BOM explosion. Always the entire master;
  // never applies the on-screen filters (that's the summary "Export"
  // above). Server decides the cost variant based on the caller's
  // role — a non-admin who calls with include_cost=true still gets a
  // cost-free file.
  const downloadAllBoms = async (withCost) => {
    try {
      setDownloadingAll(true);
      const url = `${API}/bom/export-all?format=xlsx${withCost ? "&include_cost=true" : ""}`;
      const res = await axios.get(url, { headers, responseType: "blob" });
      const stamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 13);
      saveAs(new Blob([res.data]), `bom_master_explosion_${stamp}.xlsx`);
      toast.success(
        withCost ? "Master BOM (with costs) downloaded" : "Master BOM downloaded",
      );
    } catch (e) {
      // Feb 2026 — the endpoint now streams from cache. A 503 means
      // the periodic build hasn't landed yet (or the last one FAILED).
      // Extract the JSON detail from the Blob response and show a
      // targeted toast — refresh the status card so the caption
      // reflects the FAILED/BUILDING state.
      let detail = null;
      if (e?.response?.data && e.response.data instanceof Blob) {
        try {
          const txt = await e.response.data.text();
          detail = JSON.parse(txt).detail;
        } catch (_) { /* fall through */ }
      } else {
        detail = e?.response?.data?.detail;
      }
      if (e?.response?.status === 503) {
        const msg = detail?.message
          || "Export is being generated, try again shortly.";
        toast.info(msg);
        fetchCacheStatus();
      } else {
        toast.error(detail?.message || detail || "Failed to export master BOM");
      }
    } finally {
      setDownloadingAll(false);
    }
  };

  // Feb 2026 — cache status polling for the "as of …" caption and
  // to gate the export/refresh buttons while the build is in flight.
  const fetchCacheStatus = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/bom/export-all/status`, { headers });
      setCacheMeta(r.data);
      return r.data;
    } catch (_) {
      return null;
    }
  }, [headers]);

  useEffect(() => { fetchCacheStatus(); }, [fetchCacheStatus]);

  const triggerRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const r = await axios.post(`${API}/bom/export-all/refresh`, {}, { headers });
      if (r.data?.skipped === "another_build_in_flight") {
        toast.info("A refresh is already in progress — tracking it.");
      } else {
        toast.success("Refresh started. This can take a minute or two.");
      }
      // Poll every 5s up to 3 minutes.
      const started = Date.now();
      const poll = async () => {
        const meta = await fetchCacheStatus();
        if (!meta) return;
        if (meta.status === "READY") {
          toast.success(`Cache refreshed (rows: ${meta.row_count}, SKUs: ${meta.sku_count})`);
          setRefreshing(false);
          return;
        }
        if (meta.status === "FAILED") {
          toast.error(`Refresh failed: ${meta.error || "unknown error"}`);
          setRefreshing(false);
          return;
        }
        if (Date.now() - started > 3 * 60 * 1000) {
          toast.info("Refresh still running — check back shortly.");
          setRefreshing(false);
          return;
        }
        setTimeout(poll, 5000);
      };
      setTimeout(poll, 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to trigger refresh");
      setRefreshing(false);
    }
  };

  const cacheStatus = cacheMeta?.status || "NEVER";
  const isBuilding = cacheStatus === "BUILDING" || refreshing;
  const cacheCaption = (() => {
    if (cacheStatus === "NEVER") return "Never built yet";
    if (isBuilding) return "Building…";
    if (cacheStatus === "FAILED") return `Failed: ${cacheMeta?.error || "unknown"}`;
    if (cacheMeta?.generated_at) {
      const d = new Date(cacheMeta.generated_at);
      return `As of ${d.toLocaleString()} · ${cacheMeta.sku_count || 0} SKUs`;
    }
    return "";
  })();

  const showPricingCols = canSeePricing;
  // base + pricing + expand-chevron + actions column
  const totalCols = 6 + (showPricingCols ? 4 : 0) + 1;

  return (
    <TooltipProvider>
      <div className="p-6 md:p-8" data-testid="bom-lookup-page">
        {/* Header */}
        <div className="mb-6 flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-black tracking-tight uppercase">BOM Lookup</h1>
            <p className="text-sm text-muted-foreground mt-1 font-mono">
              Filterable Buyer-SKU table · Cost · Selling Price · Margin · expand for full BOM.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={exportMain} data-testid="bom-export-main" disabled={!rows.length}>
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            {/* Bulk master-BOM export — always the entire master; ignores
                on-screen filters (that's what the summary "Export" above
                does). Master admins see the extra cost-variant button;
                everyone else sees only the cost-free option. The server
                (`_is_master_admin`) is the real arbiter — this UI toggle
                is a discoverability affordance, not a security boundary. */}
            {isMasterAdmin && (
              <Button
                variant="outline"
                onClick={() => downloadAllBoms(true)}
                disabled={downloadingAll || isBuilding || cacheStatus !== "READY"}
                data-testid="bom-export-all-with-cost"
              >
                {downloadingAll
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <Download className="w-4 h-4 mr-2" />}
                Export All BOMs (with costs)
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => downloadAllBoms(false)}
              disabled={downloadingAll || isBuilding || cacheStatus !== "READY"}
              data-testid="bom-export-all"
              title="Entire master BOM explosion — served from the 6-hourly cache"
            >
              {downloadingAll
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Download className="w-4 h-4 mr-2" />}
              Export All BOMs
            </Button>
            {isMasterAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={triggerRefresh}
                disabled={isBuilding}
                data-testid="bom-export-refresh-now"
                title="Rebuild the cache now (master admin only)"
              >
                {refreshing || cacheStatus === "BUILDING"
                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  : <RefreshCw className="w-4 h-4 mr-2" />}
                Refresh now
              </Button>
            )}
            <span
              className="text-xs text-muted-foreground font-mono px-2"
              data-testid="bom-export-cache-caption"
            >
              {cacheCaption}
            </span>
          </div>
        </div>

        {/* Sticky filter bar */}
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border border-zinc-200 rounded-sm p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Select value={filterVertical} onValueChange={setFilterVertical}>
              <SelectTrigger data-testid="bom-filter-vertical"><SelectValue placeholder="Vertical" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Verticals</SelectItem>
                {verticals.map((v) => <SelectItem key={v.id || v.name} value={v.name}>{v.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterModel} onValueChange={setFilterModel} disabled={filterVertical === "ALL"}>
              <SelectTrigger data-testid="bom-filter-model"><SelectValue placeholder="Model" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Models</SelectItem>
                {models.map((m) => <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterLicense} onValueChange={setFilterLicense}>
              <SelectTrigger data-testid="bom-filter-license"><SelectValue placeholder="License" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Licenses</SelectItem>
                <SelectItem value="(none)">(none)</SelectItem>
                {licenses.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterBrand} onValueChange={setFilterBrand}>
              <SelectTrigger data-testid="bom-filter-brand"><SelectValue placeholder="Brand" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Brands</SelectItem>
                {brands.map((b) => <SelectItem key={b.id || b.name} value={b.name}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative col-span-1 md:col-span-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search Buyer SKU, name, or Bidso ID"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-10 font-mono"
                data-testid="bom-filter-search"
              />
              {(filterVertical !== "ALL" || filterModel !== "ALL" || filterBrand !== "ALL" || filterLicense !== "ALL" || filterStatus !== "ALL" || searchText) && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="absolute right-1 top-1/2 -translate-y-1/2 h-7 px-2" data-testid="bom-clear-filters">
                  <X className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>

          {/* Status chip row */}
          {showPricingCols && (
            <div className="flex flex-wrap gap-2 mt-3" data-testid="bom-status-chips">
              {STATUS_CHIPS.map((c) => {
                const active = filterStatus === c.key;
                const count = c.key === "ALL"
                  ? (totals?.row_count ?? 0)
                  : (totals?.[c.key] ?? 0);
                return (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => setFilterStatus(c.key)}
                    data-testid={`bom-status-chip-${c.key}`}
                    className={`px-3 py-1 rounded-full text-xs font-mono border transition-all ${
                      active ? "bg-zinc-900 text-white border-zinc-900"
                             : `${c.cls || "border-zinc-200 text-zinc-600 bg-white"} hover:border-zinc-400`
                    }`}
                  >
                    {c.label} · {count}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Results table */}
        <div className="border border-zinc-200 rounded-sm overflow-hidden bg-white">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="bom-results-table">
              <thead className="bg-zinc-50 border-b border-zinc-200">
                <tr>
                  <th className="w-8 px-2 py-2" />
                  <th className="px-3 py-2 text-left font-mono text-xs font-medium text-zinc-500 uppercase">Buyer SKU ID</th>
                  <th className="px-3 py-2 text-left font-mono text-xs font-medium text-zinc-500 uppercase">Vertical</th>
                  <th className="px-3 py-2 text-left font-mono text-xs font-medium text-zinc-500 uppercase">Model</th>
                  <th className="px-3 py-2 text-left font-mono text-xs font-medium text-zinc-500 uppercase">License</th>
                  <th className="px-3 py-2 text-left font-mono text-xs font-medium text-zinc-500 uppercase">Brand</th>
                  {showPricingCols && (
                    <>
                      <th className="px-3 py-2 text-right font-mono text-xs font-medium text-zinc-500 uppercase">Cost</th>
                      <th className="px-3 py-2 text-right font-mono text-xs font-medium text-zinc-500 uppercase">Selling Price</th>
                      <th className="px-3 py-2 text-right font-mono text-xs font-medium text-zinc-500 uppercase">Margin %</th>
                      <th className="px-3 py-2 text-left font-mono text-xs font-medium text-zinc-500 uppercase">Status</th>
                    </>
                  )}
                  <th className="px-3 py-2 text-center font-mono text-xs font-medium text-zinc-500 uppercase w-20">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={totalCols} className="p-12 text-center text-zinc-500 font-mono text-sm">
                      <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2" /> Loading…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={totalCols} className="p-12 text-center text-zinc-500 font-mono text-sm">
                      No SKUs match the current filters.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const isOpen = expanded.has(r.buyer_sku_id);
                    return (
                      <RowWithExpand
                        key={r.buyer_sku_id}
                        row={r}
                        isOpen={isOpen}
                        onToggle={() => toggleExpand(r.buyer_sku_id)}
                        onOpenHistory={setHistorySkuId}
                        showPricingCols={showPricingCols}
                        bomDetail={bomCache[r.buyer_sku_id]}
                        bomLoading={bomLoading[r.buyer_sku_id]}
                        colSpan={totalCols}
                      />
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <BomHistoryDialog
          open={!!historySkuId}
          buyerSkuId={historySkuId}
          onClose={() => setHistorySkuId(null)}
        />

        {!showPricingCols && (
          <div className="mt-3 text-xs font-mono text-zinc-500 flex items-center gap-2" data-testid="bom-rbac-banner">
            <Lock className="w-3.5 h-3.5" /> Cost / Selling Price / Margin / Status hidden — requires Forecast access.
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

// Persistent row + expand panel, broken out so React only re-renders the
// row that actually toggled.
const RowWithExpand = ({ row, isOpen, onToggle, onOpenHistory, showPricingCols, bomDetail, bomLoading, colSpan }) => {
  const ChevronIcon = isOpen ? ChevronDown : ChevronRight;
  return (
    <>
      <tr
        className={`border-b border-zinc-100 hover:bg-zinc-50/60 cursor-pointer ${isOpen ? "bg-zinc-50" : ""}`}
        onClick={onToggle}
        data-testid={`bom-row-${row.buyer_sku_id}`}
      >
        <td className="px-2 py-2 align-top text-zinc-400">
          <ChevronIcon className="w-4 h-4" />
        </td>
        <td className="px-3 py-2 align-top">
          <div className="font-mono text-sm font-bold text-zinc-800">{row.buyer_sku_id}</div>
          {row.name && <div className="text-xs text-zinc-500">{row.name}</div>}
        </td>
        <td className="px-3 py-2 align-top text-sm text-zinc-700">{row.vertical || "—"}</td>
        <td className="px-3 py-2 align-top text-sm text-zinc-700">{row.model || "—"}</td>
        <td className="px-3 py-2 align-top text-sm">
          {row.license_code ? (
            <span
              className="inline-block px-2 py-0.5 rounded-full text-[10px] font-mono font-semibold"
              style={{ background: "#EEEDFE", color: "#3C3489" }}
              data-testid={`bom-license-pill-${row.buyer_sku_id}`}
            >
              {row.license_code}
            </span>
          ) : <span className="text-zinc-400">—</span>}
        </td>
        <td className="px-3 py-2 align-top text-sm text-zinc-700">{row.brand || "—"}</td>
        {showPricingCols && (
          <>
            <td className="px-3 py-2 align-top text-right font-mono text-sm" data-testid={`bom-cost-${row.buyer_sku_id}`}>
              {fmtINR(row.total_cost)}
            </td>
            <td className="px-3 py-2 align-top text-right font-mono text-sm" data-testid={`bom-sell-${row.buyer_sku_id}`}>
              <SellingPriceCell row={row} />
            </td>
            <td className={`px-3 py-2 align-top text-right font-mono text-sm ${marginColour(row.margin_pct)}`} data-testid={`bom-margin-${row.buyer_sku_id}`}>
              {row.margin_pct == null ? "—" : `${row.margin_pct.toFixed(1)}%`}
            </td>
            <td className="px-3 py-2 align-top">
              {statusBadge(row.bom_status, row.missing_price_count)}
            </td>
          </>
        )}
        {/* Row-level single-SKU export — replaces the standalone modal
            (Feb 2026). Click bubbling is stopped inside the popover so
            the whole-row expand-toggle doesn't fire. `no_bom` rows show
            the icon disabled with an explanatory tooltip. */}
        <td
          className="px-3 py-2 align-top text-center"
          onClick={(e) => e.stopPropagation()}
        >
          <BomRowExportPopover
            buyerSkuId={row.buyer_sku_id}
            disabled={row.bom_status === "no_bom"}
          />
          <button
            type="button"
            className="ml-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            title="View BOM change history"
            data-testid={`bom-history-btn-${row.buyer_sku_id}`}
            onClick={(e) => {
              e.stopPropagation();
              onOpenHistory?.(row.buyer_sku_id);
            }}
          >
            <History className="w-4 h-4" />
          </button>
        </td>
      </tr>
      {isOpen && (
        <tr className="border-b border-zinc-200 bg-zinc-50/40">
          <td colSpan={colSpan} className="p-4">
            <ExpandPanel
              buyerSkuId={row.buyer_sku_id}
              detail={bomDetail}
              loading={bomLoading}
              parentCost={row.total_cost}
              showPricingCols={showPricingCols}
            />
          </td>
        </tr>
      )}
    </>
  );
};

const SellingPriceCell = ({ row }) => {
  const sp = row.selling_price;
  if (sp == null) return <span className="text-zinc-400">—</span>;
  const count = row.selling_price_customer_count || 0;
  const cust = row.selling_price_customers || [];
  if (count <= 1) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="underline decoration-dotted decoration-zinc-400 cursor-help">
            {fmtINR(sp)}
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-sm">
          <div className="text-xs">
            <div className="font-semibold mb-1">{row.selling_price_source_customer || "Single customer"}</div>
            {row.selling_price_source_created_at && (
              <div className="text-zinc-500">Uploaded {new Date(row.selling_price_source_created_at).toLocaleDateString()}</div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="underline decoration-dotted decoration-zinc-400 cursor-help">
          {fmtINR(sp)}
          <span className="ml-1 text-[10px] text-zinc-500">×{count}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-md">
        <div className="text-xs space-y-1">
          <div className="font-semibold border-b pb-1">
            Median of {count} customer prices · {fmtINR(row.selling_price_min)} – {fmtINR(row.selling_price_max)}
          </div>
          {cust.map((c, idx) => (
            <div key={idx} className={`flex items-center justify-between gap-3 ${c.is_used ? "font-bold text-emerald-700" : ""}`}>
              <span>{c.customer_name || c.customer_id}</span>
              <span className="font-mono">
                {fmtINR(c.unit_price)}
                <span className="ml-2 text-[10px] text-zinc-500">
                  {c.created_at ? new Date(c.created_at).toLocaleDateString() : ""}
                </span>
                {c.is_used && <span className="ml-2 text-[10px] uppercase">used</span>}
              </span>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};

const ExpandPanel = ({ buyerSkuId, detail, loading, parentCost, showPricingCols }) => {
  if (loading) {
    return <div className="text-center py-6 text-zinc-500 text-sm font-mono">
      <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading BOM…
    </div>;
  }
  if (!detail) {
    return <div className="text-center py-6 text-zinc-500 text-sm font-mono">No BOM data.</div>;
  }
  const items = detail.items || [];
  if (items.length === 0) {
    return <div className="text-center py-6 text-zinc-500 text-sm font-mono">This SKU has no BOM defined.</div>;
  }
  const totalLineCost = items.reduce((acc, ln) => acc + (Number(ln.line_cost) || 0), 0);
  return (
    <div data-testid={`bom-expand-${buyerSkuId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-bold uppercase tracking-tight">BOM for {buyerSkuId}</div>
        <a
          href={`/reports?tab=buyer-sku-bom-cost&sku=${buyerSkuId}`}
          className="text-xs text-blue-600 hover:underline flex items-center gap-1"
          data-testid={`bom-detail-link-${buyerSkuId}`}
        >
          View full detail <ExternalLink className="w-3 h-3" />
        </a>
      </div>
      <div className="overflow-x-auto bg-white border border-zinc-200 rounded-sm">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              <th className="px-3 py-2 text-left font-mono text-[10px] font-medium text-zinc-500 uppercase">RM ID</th>
              <th className="px-3 py-2 text-left font-mono text-[10px] font-medium text-zinc-500 uppercase">Cat</th>
              <th className="px-3 py-2 text-left font-mono text-[10px] font-medium text-zinc-500 uppercase">Description</th>
              <th className="px-3 py-2 text-right font-mono text-[10px] font-medium text-zinc-500 uppercase">Qty</th>
              <th className="px-3 py-2 text-left font-mono text-[10px] font-medium text-zinc-500 uppercase">UOM</th>
              {showPricingCols && (
                <>
                  <th className="px-3 py-2 text-right font-mono text-[10px] font-medium text-zinc-500 uppercase">Unit Price</th>
                  <th className="px-3 py-2 text-right font-mono text-[10px] font-medium text-zinc-500 uppercase">Line Cost</th>
                </>
              )}
              <th className="px-3 py-2 text-left font-mono text-[10px] font-medium text-zinc-500 uppercase">Source</th>
            </tr>
          </thead>
          <tbody>
            {items.map((ln) => {
              const isUnpriced = showPricingCols && ((ln.price_source == null) || ((ln.avg_price || 0) === 0));
              return (
                <tr
                  key={ln.rm_id}
                  className={`border-b border-zinc-100 ${isUnpriced ? "bg-amber-50/60" : ""}`}
                  data-testid={`bom-line-${ln.rm_id}`}
                >
                  <td className="px-3 py-1.5 font-mono text-xs">{ln.rm_id}</td>
                  <td className="px-3 py-1.5">
                    {ln.category && (
                      <Badge variant="outline" className="text-[9px]">{ln.category}</Badge>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-sm text-zinc-700 max-w-[300px] truncate" title={ln.rm_name || ""}>
                    {ln.rm_name || "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono">{ln.quantity}</td>
                  <td className="px-3 py-1.5 text-xs">{ln.unit || "PCS"}</td>
                  {showPricingCols && (
                    <>
                      <td className="px-3 py-1.5 text-right font-mono">
                        {ln.avg_price == null || ln.avg_price === 0
                          ? <span className="text-amber-700">No price</span>
                          : fmtINR(ln.avg_price)}
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono font-semibold">
                        {ln.line_cost == null ? "—" : fmtINR(ln.line_cost)}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-1.5">
                    <Badge variant="outline" className={
                      ln.bom_source === "brand_specific"
                        ? "border-violet-300 text-violet-700 text-[9px]"
                        : "border-zinc-300 text-zinc-600 text-[9px]"
                    }>
                      {ln.bom_source === "brand_specific" ? "Brand" : "Common"}
                    </Badge>
                  </td>
                </tr>
              );
            })}
            {showPricingCols && (
              <tr className="bg-zinc-100 font-bold border-t-2 border-zinc-300" data-testid={`bom-total-${buyerSkuId}`}>
                <td colSpan={6} className="px-3 py-2 text-right">Total cost</td>
                <td className="px-3 py-2 text-right font-mono">{fmtINR(parentCost ?? totalLineCost)}</td>
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BomLookup;
